import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TrekCard from '@/components/TrekCard';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { getAllTreks } from '@/lib/trek-data';
import { Trek } from '@/lib/trek-data';

export const FeaturedTreksSection = () => {
  const [featuredTreks, setFeaturedTreks] = useState<Trek[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedTreks = async () => {
      try {
        const allTreks = await getAllTreks();
        
        // Filter to get only top treks
        const topTreks = allTreks.filter(trek => trek.isTopTrek);
        
        // Sort by priority (higher values first, null values last)
        const sortedTreks = topTreks.sort((a, b) => {
          // If both have priority, sort by priority (descending)
          if (a.priority !== undefined && b.priority !== undefined) {
            return b.priority - a.priority;
          }
          // If only a has priority, a comes first
          if (a.priority !== undefined) return -1;
          // If only b has priority, b comes first
          if (b.priority !== undefined) return 1;
          // If neither has priority, keep original order
          return 0;
        });
        
        // Use top treks if available, otherwise fallback to first 3 treks
        setFeaturedTreks(sortedTreks.length > 0 ? sortedTreks.slice(0, 3) : allTreks.slice(0, 3));
      } catch (error) {
        console.error('Error fetching featured treks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedTreks();
  }, []);

  return (
    <section className="py-16 md:py-24 bg-transparent">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10 md:mb-14">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-nature-800 dark:text-nature-200">Featured Treks</h2>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              Explore our handpicked selection of the most breathtaking treks in India, each offering unique experiences and stunning landscapes.
            </p>
          </div>
          <Link to="/treks" className="mt-4 md:mt-0">
            <Button variant="outline" className="group border-nature-300 dark:border-nature-700/50 hover:border-nature-500 hover:bg-nature-50 dark:hover:bg-nature-800/20">
              View all treks
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1 text-nature-600" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((_, i) => (
              <div key={i} className="h-72 rounded-xl bg-nature-100 dark:bg-nature-800/20 animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredTreks.map((trek, index) => (
              <TrekCard key={trek.id} trek={trek} delay={index} hideFeatureBadge={true} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedTreksSection;
